import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Modal } from "../../components/Modal";
import type { WorkContext } from "../../types";
import {
  assignDirectActivityMaterialDeliverable,
  getActivityMaterialDeliverableCatalog,
  getActivityMaterialDeliverables,
  setActivityMaterialDeliverableArea,
  setDirectActivityMaterialDeliverableActive,
} from "./api";
import type { ActivityMaterialDeliverable, ContractActivityMaterial, ContractArea, DeliverableCatalogItem } from "./types";
import { qualityError } from "./utils";

interface Props {
  context: WorkContext;
  material: ContractActivityMaterial | null;
  canEdit: boolean;
  refreshToken: number;
  notify: (message: string, error?: boolean) => void;
}

export function ActivityMaterialDeliverablesPanel({ context, material, canEdit, refreshToken, notify }: Props) {
  const [items, setItems] = useState<ActivityMaterialDeliverable[]>([]);
  const [areas, setAreas] = useState<ContractArea[]>([]);
  const [catalog, setCatalog] = useState<DeliverableCatalogItem[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const requestSequence = useRef(0);
  const selectionKey = `${context.proyecto_id}:${context.contrato_id}:${material?.id ?? ""}`;
  const currentSelection = useRef(selectionKey);
  currentSelection.current = selectionKey;

  const load = useCallback(async () => {
    const request = ++requestSequence.current;
    if (!material) { setItems([]); setAreas([]); setLoading(false); return; }
    setItems([]);
    setLoading(true);
    try {
      const detail = await getActivityMaterialDeliverables(context, material.id);
      if (request === requestSequence.current && currentSelection.current === selectionKey) {
        setItems(detail.entregables); setAreas(detail.areas);
      }
    } catch (error) {
      if (request === requestSequence.current && currentSelection.current === selectionKey) notify(qualityError(error), true);
    } finally {
      if (request === requestSequence.current && currentSelection.current === selectionKey) setLoading(false);
    }
  }, [context, material, notify, selectionKey]);

  useEffect(() => { void load(); return () => { requestSequence.current += 1; }; }, [load, refreshToken]);
  useEffect(() => { setCatalog([]); setCatalogLoaded(false); setOpenAdd(false); }, [context.proyecto_id, context.contrato_id]);

  async function openAssignment() {
    if (catalogLoaded) { setOpenAdd(true); return; }
    try {
      const data = await getActivityMaterialDeliverableCatalog(context);
      setCatalog(data.entregables); setAreas(data.areas); setCatalogLoaded(true); setOpenAdd(true);
    } catch (error) { notify(qualityError(error), true); }
  }

  async function changeArea(item: ActivityMaterialDeliverable, areaId: string) {
    setBusy(`area:${item.entregable_id}`);
    try { await setActivityMaterialDeliverableArea(context, item.entregable_id, areaId); await load(); notify("Área responsable actualizada."); }
    catch (error) { notify(qualityError(error), true); } finally { setBusy(""); }
  }

  async function removeDirect(item: ActivityMaterialDeliverable) {
    if (!material) return;
    setBusy(`direct:${item.entregable_id}`);
    try {
      await setDirectActivityMaterialDeliverableActive(context, material.id, item.entregable_id);
      await load();
      notify(item.heredado ? "Se retiró la asignación directa; el entregable continúa heredado por especificación." : "Asignación directa retirada.");
    } catch (error) { notify(qualityError(error), true); } finally { setBusy(""); }
  }

  return <section className="panel spec-list-panel">
    <header><div><h2>Entregables</h2><span>{material ? `Para ${material.nombre_contractual || material.nombre}` : "Selecciona un material o actividad"}</span></div>
      {material && canEdit && <button className="button primary small quality-primary-cta" disabled={!material.is_active || Boolean(busy)} onClick={() => void openAssignment()}><Plus size={15} /> Entregable directo</button>}
    </header>
    {!material ? <div className="quality-placeholder compact"><strong>Selecciona un material o actividad.</strong></div>
      : loading ? <div className="quality-placeholder compact">Cargando entregables…</div>
      : !items.length ? <div className="quality-placeholder compact"><strong>No hay entregables directos ni heredados.</strong>{canEdit && <button className="button primary small" onClick={() => void openAssignment()}><Plus size={15} /> Entregable directo</button>}</div>
      : <div className="table-wrap"><table className="compact-table"><thead><tr><th>Entregable</th><th>Origen</th><th>Especificación</th><th>Área responsable</th><th>Acciones</th></tr></thead>
        <tbody>{items.map((item) => <tr key={item.entregable_id}><td><strong>{item.entregable}</strong></td><td><span className="badges">{item.directo && <span className="badge contractual">Directo</span>}{item.heredado && <span className="badge generico">Heredado</span>}</span></td>
          <td>{item.especificaciones.length ? item.especificaciones.map((spec) => spec.codigo || spec.titulo || "Sin título").join(", ") : "—"}</td>
          <td><select aria-label={`Área responsable de ${item.entregable}`} value={item.area_id ?? ""} disabled={!canEdit || Boolean(busy)} onChange={(event) => void changeArea(item, event.target.value)}><option value="" disabled>Asignar área…</option>{areas.map((area) => <option key={area.id} value={area.id}>{area.area}</option>)}</select></td>
          <td>{canEdit && item.directo && <button className="icon-button danger" title="Retirar asignación directa" disabled={Boolean(busy)} onClick={() => void removeDirect(item)}><Trash2 size={15} /></button>}</td></tr>)}</tbody></table></div>}
    {openAdd && material && <DirectDeliverableDialog context={context} material={material} catalog={catalog} areas={areas} current={items} onClose={() => setOpenAdd(false)} onSaved={async () => { setOpenAdd(false); await load(); notify("Entregable directo asignado."); }} />}
  </section>;
}

function DirectDeliverableDialog({ context, material, catalog, areas, current, onClose, onSaved }: { context: WorkContext; material: ContractActivityMaterial; catalog: DeliverableCatalogItem[]; areas: ContractArea[]; current: ActivityMaterialDeliverable[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const directIds = useMemo(() => new Set(current.filter((item) => item.directo).map((item) => item.entregable_id)), [current]);
  const available = catalog.filter((item) => !directIds.has(item.id));
  const [entregableId, setEntregableId] = useState(""); const [areaId, setAreaId] = useState(""); const [busy, setBusy] = useState(false); const [error, setError] = useState("");
  async function submit(event: FormEvent) { event.preventDefault(); setBusy(true); setError(""); try { await assignDirectActivityMaterialDeliverable(context, material.id, entregableId, areaId); await onSaved(); } catch (caught) { setError(qualityError(caught)); } finally { setBusy(false); } }
  return <Modal title="Asignar entregable directo" onClose={onClose}><form className="form-grid quality-form" onSubmit={submit}>{error && <div className="notice error full">{error}</div>}<label className="full">Entregable<select required value={entregableId} onChange={(event) => setEntregableId(event.target.value)}><option value="">Selecciona del catálogo…</option>{available.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}</select></label><label className="full">Área responsable<select required value={areaId} onChange={(event) => setAreaId(event.target.value)}><option value="">Selecciona un área…</option>{areas.map((area) => <option key={area.id} value={area.id}>{area.area}</option>)}</select></label><div className="modal-actions full"><button type="button" className="button secondary" disabled={busy} onClick={onClose}>Cancelar</button><button type="submit" className="button primary" disabled={busy || !entregableId || !areaId}>{busy ? "Guardando…" : "Asignar"}</button></div></form></Modal>;
}
